"use client";

import { useState, useEffect } from "react";
import { Row, Col, Card } from "react-bootstrap";
import Animated from "../../UIKit/ProgressBars/Animated";

const DAILY_WORK_SECONDS = 8 * 60 * 60;

const secondsToHMS = (s = 0) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const getProgress = (worked = 0) =>
  Math.min(100, Math.round((worked / DAILY_WORK_SECONDS) * 100));

// Map status to Bootstrap border classes
const statusBorderClass = (status) => {
  switch (status) {
    case "working":
      return "border-success";
    case "meeting":
      return "border-info";
    case "break":
      return "border-warning";
    case "idle":
      return "border-danger";
    default:
      return "border-secondary";
  }
};
// Map status to Bootstrap text color classes
const statusColorClass = (status) => {
  switch (status) {
    case "working":
      return "text-success";
    case "meeting":
      return "text-info";
    case "break":
      return "text-warning";
    case "idle":
      return "text-danger";
    default:
      return "text-secondary";
  }
};
/**
 * Props:
 * - data: {
 *     name,
 *     status,
 *     start_time,
 *     worked_seconds,
 *     break_seconds,
 *     total_idle_seconds,
 *     timelineData: optional array of segments to recalc totals
 *   }
 */
const Congratulations = ({ data }) => {
  const [lastData, setLastData] = useState(data);

  // Persist last known data whenever it updates
  useEffect(() => {
    if (data) {
      setLastData(data);
    }
  }, [data]);

  // Recalculate totals from timeline if available
  const displayData = { ...lastData };

  displayData.meeting_seconds = displayData.meeting_seconds || 0;
  displayData.break_seconds = displayData.break_seconds || 0;
  displayData.worked_seconds = displayData.worked_seconds || 0;
  displayData.total_idle_seconds = displayData.total_idle_seconds || 0;

  if (displayData.timelineData && displayData.timelineData.length > 0) {
    const today = new Date().toDateString();

    const todaySegments = displayData.timelineData.filter(
      (seg) => new Date(seg.start_time).toDateString() === today
    );

    displayData.worked_seconds = todaySegments
      .filter((s) => s.status === "working")
      .reduce((sum, s) => sum + s.duration, 0);

    displayData.break_seconds = todaySegments
      .filter((s) => s.status === "break")
      .reduce((sum, s) => sum + s.duration, 0);

    displayData.total_idle_seconds = todaySegments
      .filter((s) => s.status === "idle")
      .reduce((sum, s) => sum + s.duration, 0);

    displayData.meeting_seconds = todaySegments
      .filter((s) => s.status === "meeting")
      .reduce((sum, s) => sum + s.duration, 0);
  }

  const progress = getProgress(displayData.worked_seconds);

  return (
    <Card
  className={`bg-white rounded-3 mb-4 ${
    displayData.status ? `border-1 ${statusBorderClass(displayData.status)}` : "border-0"
  }`}
>
      <Card.Body className="p-4">
        <h4 className="mb-1" title={displayData.name}>
          {displayData.name.length > 20
            ? `${displayData.name.slice(0, 20)}...`
            : displayData.name}
        </h4>
        <p className="mb-3 d-flex align-items-center gap-2">
          <span
            className={`rounded-circle ${statusColorClass(displayData.status)}`}
            style={{
              width: "12px",
              height: "12px",
              display: "inline-block",
              backgroundColor: "currentColor",
            }}
          ></span>

          <span>
            Status: <b>{displayData.status || "offline"}</b>
          </span>
        </p>

        {/* Progress bar */}
        <Animated
          value={progress}
          status={displayData.status}
          tooltipText={`Worked: ${secondsToHMS(displayData.worked_seconds)}`}
        />

        {/* Below progress bar (2-line layout) */}
        <Row className="mb-3">
          <Col>
            <div className="fw-semibold">
              {displayData.start_time
                ? new Date(displayData.start_time).toLocaleTimeString()
                : "--"}
            </div>
            <small className="text-muted">Clock In</small>
          </Col>

          <Col className="text-end">
            <div className="fw-semibold">
              {secondsToHMS(displayData.worked_seconds)}
            </div>
            <small className="text-muted">Hours Worked</small>
          </Col>
        </Row>

        <Row>
          <Col><small>Break</small></Col>
          <Col className="text-end">
            {secondsToHMS(displayData.break_seconds)}
          </Col>
        </Row>

        <Row>
          <Col><small>Idle</small></Col>
          <Col className="text-end">
            {secondsToHMS(displayData.total_idle_seconds)}
          </Col>
        </Row>

        <Row>
          <Col><small>Meeting</small></Col>
          <Col className="text-end">
            {secondsToHMS(displayData.meeting_seconds)}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default Congratulations;